import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate } from 'react-router-dom'
import { usePortalAuth } from '../auth/authContexts'
import { UserIcon, LogOutIcon, RefreshIcon, HelpIcon, MailIcon, ExternalLinkIcon, XCloseIcon } from './ui'
import HeaderDropdown from './HeaderDropdown'
import { CLUB4X4_CONTACT_US_HREF, CLUB4X4_SELF_SERVICE_HREF } from '../claims/contactLinks'

const MOBILE_NAV_ITEM_CLASS =
  'block text-left px-5 py-3 font-display font-semibold text-md leading-md hover:bg-white/10'

function MobileMenuHamburgerIcon({ open, className = '' }: { open: boolean; className?: string }) {
  const bar = 'absolute left-0 block h-0.5 w-6 rounded-full bg-current transition duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]'
  return (
    <span className={`relative inline-block size-6 shrink-0 ${className}`} aria-hidden>
      <span className={`${bar} ${open ? 'top-[11px] rotate-45' : 'top-[5px]'}`} />
      <span className={`${bar} ${open ? 'top-[11px] scale-x-0 opacity-0' : 'top-[11px]'}`} />
      <span className={`${bar} ${open ? 'top-[11px] -rotate-45' : 'top-[17px]'}`} />
    </span>
  )
}

export const QUOTES_HREF =
  'https://envest.entsia.io/channel/club-4x4-direct-v2?pr=4X4_MOTOR'

export default function DashboardNavigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { logout, isAuthenticated } = usePortalAuth()
  const navigate = useNavigate()

  const navLinks = [
    { label: 'Dashboard', to: '/dashboard' },
    { label: 'Quotes', to: QUOTES_HREF, external: true },
  ]

  const handleSignOut = async () => {
    try {
      if (isAuthenticated) {
        await logout()
      } else {
        navigate('/', { replace: true })
      }
    } catch (error) {
      console.error('Error signing out:', error)
      navigate('/', { replace: true })
    }
  }

  const handleRefreshData = () => {
    window.dispatchEvent(new CustomEvent('club4x4:refreshDashboard'))
  }

  const helpItems = [
    {
      label: 'Contact us',
      icon: <MailIcon className="w-5 h-5 text-text-primary-900" />,
      onClick: () => window.open(CLUB4X4_CONTACT_US_HREF, '_blank', 'noopener,noreferrer'),
    },
    {
      label: 'FAQs',
      icon: <HelpIcon className="w-5 h-5 text-text-primary-900" />,
      onClick: () =>
        window.open(CLUB4X4_SELF_SERVICE_HREF, '_blank', 'noopener,noreferrer'),
    },
  ]

  const userMenuItems = [
    {
      label: 'Refresh data',
      icon: <RefreshIcon className="w-5 h-5 text-text-primary-900" />,
      onClick: handleRefreshData,
    },
    {
      label: 'Sign out',
      icon: <LogOutIcon className="w-5 h-5 text-text-primary-900" />,
      onClick: handleSignOut,
    },
  ]

  const closeMobileMenu = () => setIsMenuOpen(false)
  const openMobileMenu = () => setIsMenuOpen(true)

  const [drawerMounted, setDrawerMounted] = useState(false)
  const [drawerEnter, setDrawerEnter] = useState(false)

  useEffect(() => {
    if (isMenuOpen) {
      setDrawerMounted(true)
      setDrawerEnter(false)
      let innerRafId: number | undefined
      const outerRafId = window.requestAnimationFrame(() => {
        innerRafId = window.requestAnimationFrame(() => setDrawerEnter(true))
      })
      return () => {
        window.cancelAnimationFrame(outerRafId)
        if (innerRafId !== undefined) window.cancelAnimationFrame(innerRafId)
      }
    }
    setDrawerEnter(false)
  }, [isMenuOpen])

  useEffect(() => {
    if (!drawerMounted || isMenuOpen || drawerEnter) return
    const id = window.setTimeout(() => setDrawerMounted(false), 220)
    return () => window.clearTimeout(id)
  }, [drawerMounted, isMenuOpen, drawerEnter])

  return (
    <nav className="bg-background-brand-solid flex flex-col items-center relative shrink-0 w-full">
      {/* Mobile: Logo and hamburger */}
      <div className="flex md:hidden gap-[12px] items-center justify-between pl-[16px] pr-[8px] py-[12px] relative shrink-0 w-full h-[64px]">
        <div className="h-8 w-[120px] flex items-center">
          <img
            src="/Clip path group.svg"
            alt="Club 4X4 Insurance"
            className="h-full w-auto object-contain brightness-0 invert"
          />
        </div>
        <button
          type="button"
          onClick={() => (isMenuOpen ? closeMobileMenu() : openMobileMenu())}
          className="cursor-pointer flex items-center justify-center overflow-visible p-[8px] relative rounded-md shrink-0 text-text-primary-on-brand"
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
        >
          <MobileMenuHamburgerIcon open={isMenuOpen} />
        </button>
      </div>

      {/* Desktop: Full navigation */}
      <div className="hidden md:flex gap-[16px] items-center px-[32px] py-0 relative shrink-0 w-full h-[80px]">
        {/* Logo and Navigation */}
        <div className="flex gap-[20px] items-center min-h-px min-w-px relative shrink-0 flex-1">
          {/* Logo */}
          <div className="h-8 w-[120px] flex items-center">
            <img
              src="/Clip path group.svg"
              alt="Club 4X4 Insurance"
              className="h-full w-auto object-contain brightness-0 invert"
            />
          </div>
          {/* Navigation Links */}
          <div className="flex gap-[8px] items-center relative shrink-0">
            {navLinks.map((link) =>
              link.external ? (
                <a
                  key={link.label}
                  href={link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative flex gap-[2px] items-center justify-center overflow-visible px-[12px] py-[4px] pr-9 rounded-md shrink-0"
                >
                  <ExternalLinkIcon
                    className="pointer-events-none absolute top-2 right-2 size-4 text-text-primary-on-brand"
                    aria-hidden
                  />
                  <span className="font-display font-semibold text-md leading-md text-text-primary-on-brand">
                    {link.label}
                  </span>
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.to}
                  className="flex gap-[2px] items-center justify-center overflow-clip px-[12px] py-[4px] relative rounded-md shrink-0"
                  onClick={link.to === '/dashboard' ? () => sessionStorage.removeItem('club4x4_policies_cache') : undefined}
                >
                  <span className="font-display font-semibold text-md leading-md text-text-primary-on-brand">
                    {link.label}
                  </span>
                </Link>
              )
            )}
            <HeaderDropdown
              trigger={
                <span className="font-display font-semibold text-md leading-md text-text-primary-on-brand">
                  Help
                </span>
              }
              triggerClassName="cursor-pointer flex gap-[2px] items-center justify-center overflow-clip px-[12px] py-[4px] relative rounded-md shrink-0"
              items={helpItems}
              ariaLabel="Help menu"
              align="left"
            />
          </div>
        </div>

        {/* User Avatar - Right side with Dropdown */}
        <HeaderDropdown
          trigger={
            <div className="bg-[#e8e8e8] overflow-clip relative rounded-full shrink-0 size-10 flex items-center justify-center">
              <UserIcon className="w-5 h-5 text-[#6b7280]" />
            </div>
          }
          triggerClassName="cursor-pointer flex flex-col items-center justify-center relative shrink-0"
          items={userMenuItems}
          ariaLabel="User menu"
        />
      </div>

      {/* Mobile menu: single sheet (portaled), enter/exit transitions */}
      {drawerMounted &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className={`fixed inset-0 z-50 flex flex-col md:hidden transition-opacity duration-200 ease-out motion-reduce:transition-none ${
              drawerEnter ? 'opacity-100' : 'opacity-0'
            } ${!drawerEnter && isMenuOpen ? 'pointer-events-none' : ''}`}
            data-testid="mobile-nav-drawer"
          >
            <div
              className={`flex flex-1 flex-col min-h-0 bg-background-brand-solid text-text-primary-on-brand shadow-lg overflow-y-auto transition-transform duration-200 ease-out motion-reduce:transition-none ${
                drawerEnter ? 'translate-y-0' : '-translate-y-2'
              }`}
            >
              <div className="flex items-center justify-between gap-3 px-4 py-4 border-b border-white/25 shrink-0">
                <div className="h-8 w-[120px] flex items-center min-w-0">
                  <img
                    src="/Clip path group.svg"
                    alt="Club 4X4 Insurance"
                    className="h-full w-auto max-w-full object-contain object-left brightness-0 invert"
                  />
                </div>
                <button
                  type="button"
                  onClick={closeMobileMenu}
                  className="cursor-pointer flex items-center justify-center p-2 rounded-md shrink-0 text-text-primary-on-brand hover:bg-white/10"
                  aria-label="Close menu"
                >
                  <XCloseIcon className="w-6 h-6" aria-hidden />
                </button>
              </div>

              <div className="flex flex-col flex-1">
                <div className="flex flex-col">
                  <Link
                    to="/dashboard"
                    onClick={() => {
                      closeMobileMenu()
                      sessionStorage.removeItem('club4x4_policies_cache')
                    }}
                    className={MOBILE_NAV_ITEM_CLASS}
                  >
                    Dashboard
                  </Link>
                  <a
                    href={QUOTES_HREF}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMobileMenu}
                    className={`relative pr-14 ${MOBILE_NAV_ITEM_CLASS}`}
                  >
                    <ExternalLinkIcon
                      className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 size-4 text-text-primary-on-brand"
                      aria-hidden
                    />
                    Quotes
                  </a>
                  <a
                    href={CLUB4X4_SELF_SERVICE_HREF}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMobileMenu}
                    className={MOBILE_NAV_ITEM_CLASS}
                  >
                    FAQs
                  </a>
                  <a
                    href={CLUB4X4_CONTACT_US_HREF}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMobileMenu}
                    className={MOBILE_NAV_ITEM_CLASS}
                  >
                    Contact us
                  </a>
                </div>

                <div className="flex flex-col mt-1">
                  <button
                    type="button"
                    onClick={() => {
                      closeMobileMenu()
                      handleSignOut()
                    }}
                    className="flex w-full items-center gap-3 text-left px-5 py-3 font-display font-semibold text-md leading-md hover:bg-white/10"
                  >
                    <LogOutIcon className="w-5 h-5 shrink-0 text-text-primary-on-brand" aria-hidden />
                    <span>Log out</span>
                  </button>
                </div>
              </div>
            </div>

            <button
              type="button"
              className={`flex-1 w-full min-h-[min(40vh,200px)] shrink-0 bg-black/40 transition-opacity duration-200 ease-out motion-reduce:transition-none ${
                drawerEnter ? 'opacity-100' : 'opacity-0'
              }`}
              aria-label="Close menu"
              onClick={closeMobileMenu}
            />
          </div>,
          document.body
        )}
    </nav>
  )
}
